import type { ReactNode } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import type { TextInputProps } from 'react-native';

import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type InputProps = TextInputProps & {
  label: string;
  error?: string;
  rightAccessory?: ReactNode;
};

export function Input({
  label,
  error,
  rightAccessory,
  style,
  accessibilityLabel,
  ...props
}: InputProps) {
  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      {rightAccessory ? (
        <View style={[styles.inputFrame, error ? styles.inputError : null]}>
          <TextInput
            accessibilityLabel={accessibilityLabel ?? label}
            autoCapitalize="none"
            placeholderTextColor={colors.mutedText}
            selectionColor={colors.primary}
            style={[styles.input, styles.inputFramed, style]}
            {...props}
          />
          <View style={styles.rightAccessory}>{rightAccessory}</View>
        </View>
      ) : (
        <TextInput
          accessibilityLabel={accessibilityLabel ?? label}
          autoCapitalize="none"
          placeholderTextColor={colors.mutedText}
          selectionColor={colors.primary}
          style={[styles.input, error ? styles.inputError : null, style]}
          {...props}
        />
      )}
      {error ? <Text style={styles.error}>{error}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: spacing.xs,
  },
  label: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '700',
  },
  input: {
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    color: colors.text,
    fontSize: 16,
    minHeight: 48,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  inputFrame: {
    alignItems: 'center',
    backgroundColor: colors.inputBackground,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    flexDirection: 'row',
    minHeight: 48,
  },
  inputFramed: {
    borderWidth: 0,
    flex: 1,
    minHeight: 46,
    minWidth: 0,
    paddingRight: spacing.xs,
  },
  inputError: {
    borderColor: colors.danger,
  },
  rightAccessory: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: spacing.xs,
  },
  error: {
    color: colors.danger,
    fontSize: 13,
  },
});
